import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';

// ROLLBACK:
//     Set .env: REACT_APP_USE_COMBINED_INVENTORY_MODAL=false
//     The wrapper (RRDInventoryModalWrapper) will fall back to the originals.

import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Container,
  Input,
  SelectDropdown,
  Col,
  Row,
  Grid,
} from '@uhg-uicl/ui-core/dist';
import { IFacilityItem, IPayerAddressItem, IPayerMasterItem, IRRDInventoryDetails } from '../Infrastructure/Interfaces';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../Store/StoreInit';
import { fetchPayerMasterList } from '../Store/PayerMasterReducer';
import { fetchPayerAddressList } from '../Store/GetDropDownsValuesReducer';
import { message } from 'antd';
import axios from 'axios';
import {
  columns,
  docTypeHierarchy,
  getDocTypeOptionsForClient,
  getPackageAbbreviation,
  mailTypeOptions,
} from './RRDCombinedInventoryConfig';
import {
  ADDRESS_REQUIRED_SEND_TO,
  createEmptyValidationErrors,
  getClientValidationConfig,
  getValidationErrors,
  OVERRIDE_ADDRESS_REQUIRED_FIELDS,
} from './RRDCombinedInventoryValidationConfig';
import type { ValidationErrors, ValidationField } from './RRDCombinedInventoryValidationConfig';
import {
  AUTOFILL_QUALITY_THRESHOLD,
  AZURE_AI_CLASSIFICATION_ENABLED,
  analyzePdfDocument,
  classifyPDFDocumentType,
  convertPDFPagesToBase64Json,
  extractFieldsFromPDFPageWithQuality,
  validatePDFFile,
  ExtractedFields,
  ExtractedFieldsWithQuality,
} from './RRDCombinedInventoryPDFUtils';
import { classifyDocument, DocumentClassificationResult } from './RRDAzureOpenAIUtils';
import { ExtractedFacilityData, findBestFacilityMatch } from './RRDFacilityMatcher';
import './RRDCombinedInventoryModal.css';

// Feature flag - set to true to use the combined modal
const USE_COMBINED_MODAL = process.env.REACT_APP_USE_COMBINED_INVENTORY_MODAL === 'true';

interface Props {
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  rrdInventoryItem: any;
  refreshData: any;
}

interface UploadItem {
  id: number;
  selectedDocType: string;
  mailType: string;
  file: File | null;
  otherDocType: string;
  fileError?: string;
  pageCount?: number;
}

const getMailTypeForDocument = (documentType: string): string => (
  documentType === 'Medical Record' ? 'CERT' : 'STD'
);

const normalizeDocumentTypeForUpload = (documentType: string | null | undefined): string => {
  const normalized = (documentType || '').trim().toUpperCase();
  if (!normalized) return '';

  const documentTypeMap: Record<string, string> = {
    'AMBULANCE REPORT': 'Ambulance Report',
    'APPEAL LETTER': 'Appeal Letter',
    'COVER LETTER': 'Cover Letter',
    'EOB': 'EOB',
    'EXPLANATION OF BENEFITS': 'EOB',
    'IZ STATEMENT': 'IZ Statement',
    'ITEMIZED STATEMENT': 'IZ Statement',
    'ITEMIZED BILL': 'IZ Statement',
    'ITEMIZED BILL / CHARGE DETAIL': 'IZ Statement',
    'ITEMIZED STATEMENT (IZ / BILL)': 'IZ Statement',
    'CHARGE DETAIL': 'IZ Statement',
    'MEDICAL RECORD': 'Medical Record',
    'PATIENT LETTER': 'Patient Letter',
    'PATIENT LETTER WITH OVERRIDE ADDRESS': 'Patient Letter With Override Address',
    'PAYER DISPUTE FORM': 'Payer Dispute Form',
    'PROVIDER DISPUTE RESOLUTION REQUEST': 'Payer Dispute Form',
    'PAYER LETTER': 'Payer Letter',
    'RATE SHEET': 'Rate Sheet',
    'FEE SCHEDULE': 'Rate Sheet',
    'RATE SCHEDULE': 'Rate Sheet',
    'W9 FORM': 'W9 Form',
    'W-9 FORM': 'W9 Form',
    'FORM W9': 'W9 Form',
    'FORM W-9': 'W9 Form',
    'OTHER': 'Other',
    'OTHERS': 'Other',
  };

  return documentTypeMap[normalized] || documentType || '';
};

// ── Per-client address section field configuration ────────────────────────────
// Add or modify a client key here to control which fields appear in the address
// section for that client. All other clients fall back to DEFAULT_ADDRESS_FIELD_CONFIG.
type ClientAddressFieldConfig = {
  /** Show the payer name input field */
  payerName: boolean;
  /** Show the autocomplete dropdown on the payer name input (payer list search) */
  payerNameAutocomplete: boolean;
  /** Show the Display Name input field */
  displayName: boolean;
  /** Show First Name / Last Name inputs (Override Address sendTo) */
  firstLastName: boolean;
};

const CLIENT_ADDRESS_FIELD_CONFIG: Record<string, ClientAddressFieldConfig> = {
  Dignity: {
    payerName: true,
    payerNameAutocomplete: true,
    displayName: true,
    firstLastName: true,
  },
};

const DEFAULT_ADDRESS_FIELD_CONFIG: ClientAddressFieldConfig = {
  payerName: false,
  payerNameAutocomplete: false,
  displayName: false,
  firstLastName: true,
};

const EMPTY_EXTRACTED_RESULT: ExtractedFieldsWithQuality = {
  fields: {},
  fieldScores: {},
  overallScore: 0,
};

/**
 * Determines whether an uploaded document should default Request Type and
 * Send To to "Payer". True when either:
 *  - the document was classified (locally or via Azure AI) as "Payer Letter", or
 *  - a full payer address (name + address + city + state + zip) was identified.
 */
function isPayerRequestDocument(
  documentType: string | null | undefined,
  payerName: string | null | undefined,
  payerAddress: string | null | undefined,
  payerCity: string | null | undefined,
  payerState: string | null | undefined,
  payerZip: string | null | undefined,
): boolean {
  if (documentType === 'Payer Letter') return true;
  return !!(payerName && payerAddress && payerCity && payerState && payerZip);
}
// ─────────────────────────────────────────────────────────────────────────────

const RRDCombinedInventoryModal = React.memo(function RRDCombinedInventoryModal({
  isOpen = false,
  setIsOpen = () => {},
  rrdInventoryItem,
  refreshData,
}: Props) {
  const dispatch = useDispatch<AppDispatch>();

  // Redux state
  useEffect(() => {
    dispatch(fetchPayerMasterList());
    dispatch(fetchPayerAddressList());
  }, [dispatch]);

  const payermasterlist: IPayerMasterItem[] = useSelector((state: any) => {
    return state.payerMasterList.payerMasterList;
  });
  const payerAddresslist: IPayerAddressItem[] = useSelector((state: any) => {
    return state.payerAddressList.payerAddressList;
  });
  const facilityAddressList: IFacilityItem[] = useSelector((state: any) => {
    return state.facilityDetailList.facilityDetailsList;
  });

  const userdetails = localStorage.getItem('userdetails');
  const parsedUserDetails = userdetails ? JSON.parse(userdetails) : null;
  const arrregioncodes: string[] = (process.env.REACT_APP_REGION_CODES as string).split(',');

  const userid = parsedUserDetails && parsedUserDetails[0]?.portal_Id !== ''
    ? parsedUserDetails[0]?.portal_Id
    : '';

  const userClientName = parsedUserDetails && parsedUserDetails[0]?.clientName !== ''
    ? parsedUserDetails[0]?.clientName
    : '';

  // const userClientNames: string[] = Array.from(new Set<string>(
  //   userClientName
  //     .split(',')
  //     .map((clientName: string) => clientName.trim())
  //     .filter(Boolean)
  // ));
  const userClientNames = ['Dignity'];

  const defaultUserClientName = userClientNames[0] || 'Dignity';

  // Inventory form state
  const [data, setdata] = useState({
    inventoryMasterId: 0,
    transactionId: '',
    clientName: defaultUserClientName,
    lockboxAddress: '',
    region: '',
    claimNumber: '',
    accountNumber: '',
    mrn: '',
    facilityAddressId: 0,
    facilityName: '',
    facilityAddress: '',
    facilityCity: '',
    facilityState: '',
    facilityZip: '',
    facilityCode: '',
    facilityPhoneNo: '',
    sendFrom: '',
    sendTo: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    displayName: '',
    firstName: '',
    lastName: '',
    payerName: '',
    payerAddress: '',
    payerCity: '',
    payerState: '',
    PayerZip: '',
    requestType: ''
  });

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(createEmptyValidationErrors());

  // UI state
  const [showDropdown, setShowDropdown] = useState(false);
  const [payerAddressSearchText, setPayerAddressSearchText] = useState('');
  const [payerAddressShowDropdown, setPayerAddressShowDropdown] = useState(false);
  const [showGuarantorFields, setShowGuarantorFields] = useState(false);
  const [showHomeLessFields, setShowHomeLessFields] = useState(false);
  const [showBREFields, setShowBREFields] = useState(false);
  const [showPayerNameFields, setShowPayerNameFields] = useState(false);
  const [showPatientFields, setShowPatientFields] = useState(false);
  const [inProgressRecords, setInProgressRecords] = useState<any[]>([]);
  const [uploadedFileRecords, setUploadedFileRecords] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [scanStatusMessage, setScanStatusMessage] = useState('');
  // Number of PDF scan/validation operations currently running. While > 0,
  // all action buttons are disabled and a scanning message is shown.
  const [scanningCount, setScanningCount] = useState(0);
  const [refreshList, setRefreshList] = useState(false);
  const [facilityCode, setFacilityCode] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [autofillMissed, setAutofillMissed] = useState<Set<string>>(new Set());

  // Upload items state
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([
    { id: 1, selectedDocType: '', mailType: '', file: null, otherDocType: '' },
  ]);

  // Refs
  const uploadItemsRef = useRef(uploadItems);
  const inventoryDataRef = useRef(data);
  const needsAdditionalExtractionRef = useRef(true);
  const payerNameDropdownRef = useRef<HTMLDivElement | null>(null);
  const payerNameDivRef = useRef<HTMLDivElement | null>(null);
  const payerAddressDropdownRef = useRef<HTMLDivElement | null>(null);
  const payerAddressDivRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Effects
  useEffect(() => {
    if (rrdInventoryItem) {
      setdata((prevdata) => ({
        ...prevdata,
        inventoryMasterId: rrdInventoryItem?.inventoryMasterId || 0,
        transactionId: rrdInventoryItem?.transactionId || 0,
        clientName: rrdInventoryItem?.clientName || '',
        lockboxAddress: rrdInventoryItem?.senderAddress || '',
        region: rrdInventoryItem?.region || '',
        claimNumber: rrdInventoryItem?.claimNo || rrdInventoryItem?.claimNumber || '',
        accountNumber: rrdInventoryItem?.accountNumber || '',
        mrn: rrdInventoryItem?.mrn || '',
        facilityName: rrdInventoryItem?.senderName || '',
        facilityAddress: rrdInventoryItem?.senderAddress || '',
        facilityCity: rrdInventoryItem?.senderCity || '',
        facilityState: rrdInventoryItem?.senderState || '',
        facilityZip: rrdInventoryItem?.senderZip || '',
        facilityCode: rrdInventoryItem?.senderCode || '',
        facilityPhoneNo: rrdInventoryItem?.senderPhoneNo || '',
        sendFrom: rrdInventoryItem?.sendFrom || '',
        sendTo: rrdInventoryItem?.sendTo || '',
        address: rrdInventoryItem?.receiverAddress || '',
        city: rrdInventoryItem?.receiverCity || '',
        state: rrdInventoryItem?.receiverState || '',
        zip: rrdInventoryItem?.receiverZip || '',
        displayName: rrdInventoryItem?.displayName || '',
        payerName: rrdInventoryItem?.receiverName || '',
        payerAddress: rrdInventoryItem?.payerAddress || '',
        payerCity: rrdInventoryItem?.payerCity || '',
        payerState: rrdInventoryItem?.payerState || '',
        PayerZip: rrdInventoryItem?.PayerZip || '',
        requestType: rrdInventoryItem?.requestType || '',
      }));
      setFacilityCode(rrdInventoryItem?.facilityCode || rrdInventoryItem?.senderCode);
      setAddressFields(rrdInventoryItem?.sendTo);
    } else {
      setdata({
        inventoryMasterId: 0,
        transactionId: '',
        clientName: defaultUserClientName,
        lockboxAddress: '',
        region: '',
        claimNumber: '',
        accountNumber: '',
        mrn: '',
        facilityAddressId: 0,
        facilityName: '',
        facilityAddress: '',
        facilityCity: '',
        facilityState: '',
        facilityZip: '',
        facilityCode: '',
        facilityPhoneNo: '',
        sendFrom: '',
        sendTo: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        displayName: '',
        firstName: '',
        lastName: '',
        payerName: '',
        payerAddress: '',
        payerCity: '',
        payerState: '',
        PayerZip: '',
        requestType: '',
      });
    }
  }, [rrdInventoryItem, isOpen]);

  useEffect(() => {
    setValidationErrors(createEmptyValidationErrors());
    setUploadItems([{ id: 1, selectedDocType: '', mailType: '', file: null, otherDocType: '' }]);
    setAutofillMissed(new Set());
    needsAdditionalExtractionRef.current = true;
  }, [isOpen]);

  useEffect(() => {
    uploadItemsRef.current = uploadItems;
  }, [uploadItems]);

  useEffect(() => {
    inventoryDataRef.current = data;
    const currentErrors = getValidationErrors(
      data,
      getClientValidationConfig(data.clientName.trim()),
    );
    needsAdditionalExtractionRef.current = Object.values(currentErrors).some(Boolean);
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        payerNameDropdownRef.current &&
        !payerNameDropdownRef.current.contains(event.target as Node) &&
        payerNameDivRef.current &&
        !payerNameDivRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }

      if (
        payerAddressDropdownRef.current &&
        !payerAddressDropdownRef.current.contains(event.target as Node) &&
        payerAddressDivRef.current &&
        !payerAddressDivRef.current.contains(event.target as Node)
      ) {
        setPayerAddressShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch forms (in-progress items for duplicate detection on Details tab)
  const fetchForms = useCallback(async () => {
    setInProgressRecords([]);
    if (data.accountNumber.trim() !== '') {
      try {
        const baseurl = process.env.REACT_APP_RRD_OPTIMIZATION_BASE_URL as string;
        const formsResponse = await axios.get(`${baseurl}/Inventory/InProgress/Account`, {
          params: { accountNo: data.accountNumber.trim() },
          headers: { accept: 'text/plain' },
        });

        if (formsResponse.data.success && formsResponse.data.result != null) {
          setInProgressRecords(formsResponse.data.result || []);
        }
      } catch (error) {
        console.error('Error fetching forms:', error);
      }
    }
  }, [data.accountNumber]);

  // Fetch uploaded files
  const fetchUploadedFiles = useCallback(async () => {
    if (!data.inventoryMasterId) return;
    try {
      const baseurl = process.env.REACT_APP_RRD_OPTIMIZATION_BASE_URL as string;
      const url = `${baseurl}/InventoryFiles/ByInventoryMasterId/${data.inventoryMasterId}`;
      const response = await axios.get(url, {
        headers: { accept: 'text/plain' },
      });
      if (response.data.success) {
        const updatedRecords = response.data.result.map((record: any) => ({
          ...record,
          delete: {
            domID: 'button-delete-file',
            name: 'Delete File',
            buttonType: 'diminished',
            onClick: () => handleRemoveFile(record.inventoryFileId),
            disabled: false,
          },
        }));
        setUploadedFileRecords(updatedRecords);
      }
    } catch (error) {
      console.error('Error fetching uploaded files:', error);
    }
  }, [data.inventoryMasterId]);

  useEffect(() => {
    fetchForms();
  }, [data.accountNumber, fetchForms]);

  useEffect(() => {
    if (data.inventoryMasterId > 0) {
      fetchUploadedFiles();
    }
  }, [data.inventoryMasterId, refreshList, fetchUploadedFiles]);

  const isDignitySelected = data.clientName === 'Dignity';
  const clientFieldConfig = CLIENT_ADDRESS_FIELD_CONFIG[data.clientName] ?? DEFAULT_ADDRESS_FIELD_CONFIG;
  const firstUploadedItemId = useMemo(
    () => uploadItems.find((item) => item.file)?.id ?? null,
    [uploadItems]
  );
    const availableFacilityList = isDignitySelected
      ? facilityAddressList.filter((facility) => Number(facility.facilityCode) !== 11)
      : facilityAddressList;
    const activeDocTypeOptions = getDocTypeOptionsForClient(data.clientName);
    const availableFacilityOptions = availableFacilityList.map((facility) => ({
    label: `${facility.facilityCode} - ${facility.facilityName} - ${facility.lockboxAddress} - ${facility.lockboxCity} - ${facility.lockboxState} - ${facility.lockboxZip}`,
    value: facility.facilityAddressId.toString(),
  }));
  const applyFacilityDetails = useCallback((facilitydetail?: IFacilityItem) => {
    if (!facilitydetail) return;

    setdata((prevdata) => ({
      ...prevdata,
      facilityAddressId: facilitydetail.facilityAddressId,
      lockboxAddress: facilitydetail.facilityAddressId.toString(),
      facilityAddress: facilitydetail.lockboxAddress || '',
      facilityCity: facilitydetail.lockboxCity || '',
      facilityState: facilitydetail.lockboxState || '',
      facilityZip: facilitydetail.lockboxZip || '',
      facilityCode: facilitydetail.facilityCode.toString() || '',
      facilityName: facilitydetail.facilityName || '',
      facilityPhoneNo: facilitydetail.lockboxPhone || '',
      sendFrom: facilitydetail.efrFacilityName || '',
      region: facilitydetail.region.toUpperCase() || '',
    }));
  }, []);

  // Handlers
  const handleChange = (e: any, elementId: string) => {
    // Guard: some UI-library components pass `null` or `undefined` on clear/reset.
    if (e == null) return;

    // Resolve the raw value safely regardless of whether `e` is a SelectDropdown
    // option object ({ value, label }) or a standard DOM input event ({ target }).
    const resolveValue = (): string => (e?.value ?? e?.target?.value ?? '') as string;

    if (elementId === 'requestType') {
      const val = e?.value ?? '';
      setdata((prevdata) => ({
        ...prevdata,
        sendTo: val,
        requestType: val,
      }));
      setAddressFields(val || 'default');
    } else if (elementId === 'lockboxAddress') {
      const facilitydetail = facilityAddressList.find(x => x.facilityAddressId == e?.value);
      applyFacilityDetails(facilitydetail);
    } else if (elementId === 'sendTo') {
      const val = e?.value ?? '';
      setdata((prevdata) => ({
        ...prevdata,
        sendTo: val,
      }));
      setAddressFields(val);
    } else if (elementId === 'clientName') {
      const inputvalue = resolveValue();
      clearUploadSelections();

      if (inputvalue === 'Dignity') {
        setdata((prevdata) => ({
          ...prevdata,
          clientName: 'Dignity',
        }));
        setAddressFields('default');
      } else {
        setdata({
          ...data,
          clientName: inputvalue,
        });
      }
    } else {
      let inputvalue = resolveValue();
      if (inputvalue.endsWith(',')) {
        inputvalue = inputvalue.slice(0, -1);
      }
      setdata({
        ...data,
        [elementId]: inputvalue,
      });
    }
    // Clear the autofill hint when the user manually edits the field.
    setAutofillMissed((prev) => {
      if (!prev.has(elementId)) return prev;
      const next = new Set(prev);
      next.delete(elementId);
      return next;
    });
  };

  const handleRequestTypeChange = (e: any) => {
    setdata((prevdata) => ({
      ...prevdata,
      requestType: e.value,
      sendTo: e.value,
    }));
    setAddressFields(e.value || 'default', e.value || '');
  };

  function setAddressFields(value: string, requestTypeValue?: string) {
    const activeRequestType = (requestTypeValue ?? data.requestType ?? '').trim();
    const showPayerAddress = value === 'Payer' && activeRequestType === 'Payer';
    const fieldStates: { [key: string]: any } = {
      Guarantor: { showGuarantorFields: true, showHomeLessFields: false, showPayerNameFields: false, showPatientFields: false, showBREFields: false },
      HomeLess: { showGuarantorFields: false, showHomeLessFields: true, showPayerNameFields: false, showPatientFields: false, showBREFields: false },
      Payer: { showGuarantorFields: false, showHomeLessFields: false, showPayerNameFields: showPayerAddress, showPatientFields: false, showBREFields: false },
      Patient: { showGuarantorFields: false, showHomeLessFields: false, showPayerNameFields: false, showPatientFields: true, showBREFields: false },
      'Override Address': { showGuarantorFields: false, showHomeLessFields: false, showPayerNameFields: false, showPatientFields: false, showBREFields: true },
      default: { showGuarantorFields: false, showHomeLessFields: false, showPayerNameFields: false, showPatientFields: false, showBREFields: false },
    };

    const newState = fieldStates[value] || fieldStates.default;
    setShowGuarantorFields(newState.showGuarantorFields);
    setShowHomeLessFields(newState.showHomeLessFields);
    setShowPayerNameFields(newState.showPayerNameFields);
    setShowPatientFields(newState.showPatientFields);
    setShowBREFields(newState.showBREFields);
  }

  const showPayerAddressFields = data.sendTo.trim() === 'Payer' && data.requestType.trim() === 'Payer';

  const filteredOptions = (payermasterlist || [])
    .filter((payer) => payer.isActive && payer.payerName.toLowerCase().includes(data.payerName.toLowerCase()))
    .map((payer) => ({
      label: payer.payerName,
      value: payer.payerName,
    }));

  const payerAddressFilteredOptions = (payerAddresslist || [])
    .filter((address) =>
      address.isActive && (
        payerAddressSearchText.trim() !== ''
          ? (
            address.payerName.toLowerCase().includes(payerAddressSearchText.toLowerCase()) ||
            address.address.toLowerCase().includes(payerAddressSearchText.toLowerCase()) ||
            address.city.toLowerCase().includes(payerAddressSearchText.toLowerCase()) ||
            address.state.toLowerCase().includes(payerAddressSearchText.toLowerCase()) ||
            address.zip.toLowerCase().includes(payerAddressSearchText.toLowerCase())
          )
          : data.payerName.trim() !== ''
            ? address.payerName.toLowerCase() === data.payerName.toLowerCase()
            : true
      )
    )
    .map((address) => ({
      label: address.payerName + ' - ' + address.address + ' - ' + address.city + ' - ' + address.state + ' - ' + address.zip,
      value: address.payerAddressId.toString(),
    }));

  const handleOptionClick = (option: { label: string; value: string }) => {
    setdata((prevdata) => ({ ...prevdata, payerName: option.label }));
    setShowDropdown(false);
    if (option.label != '') {
      setPayerAddressSearchText('');
    }
  };

  const handlePayerAddressOptionClick = (option: { label: string; value: string }) => {
    const selectedAddress = payerAddresslist.find((x) => x.payerAddressId === Number(option.value));
    if (selectedAddress) {
      setdata({
        ...data,
        payerName: selectedAddress.payerName || '',
        address: selectedAddress.address || '',
        city: selectedAddress.city || '',
        state: selectedAddress.state || '',
        zip: selectedAddress.zip || '',
      });
    }
    setPayerAddressSearchText(option.value);
    setPayerAddressShowDropdown(false);
  };

  // Upload handlers
  const handleAddMore = useCallback(() => {
    setUploadItems((prevItems) => [
      ...prevItems,
      { id: prevItems.length + 1, selectedDocType: '', mailType: '', file: null, otherDocType: '' },
    ]);
  }, []);

  const handleRemoveItem = useCallback((id: number) => {
    setUploadItems((prevItems) => prevItems.filter((item) => item.id !== id));
  }, []);

  // Clears the selected file for an item (without removing the item row), so the
  // user can re-select/overwrite it. Works for every item, including the first.
  const handleClearFile = useCallback((id: number) => {
    setUploadItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, file: null, fileError: undefined, pageCount: undefined } : item
      )
    );
  }, []);

  const clearUploadSelections = useCallback(() => {
    setUploadItems((prevItems) =>
      prevItems.map((item) => ({
        ...item,
        selectedDocType: '',
        mailType: '',
        otherDocType: '',
      }))
    );
  }, []);

  const getPatientNameParts = useCallback((patientName: string | null | undefined) => {
    if (!patientName) {
      return { firstName: '', lastName: '' };
    }

    const parts = patientName.split(/\s+/).filter(Boolean);
    return {
      firstName: parts.slice(0, -1).join(' '),
      lastName: parts.slice(-1).join(' '),
    };
  }, []);

  const findMatchedFacility = useCallback((extractedFacility: ExtractedFacilityData) => (
    findBestFacilityMatch(extractedFacility, availableFacilityList)
  ), [availableFacilityList]);

  const handleItemChange = useCallback((id: number, field: string, value: any) => {
    setUploadItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id
          ? {
              ...item,
              [field]: value,
              mailType:
                field === 'selectedDocType'
                  ? getMailTypeForDocument(value)
                  : field === 'mailType'
                  ? value
                  : item.mailType,
            }
          : item
      )
    );
  }, []);

  const handleFileChange = useCallback(async (id: number, file: File | null) => {
    setUploadItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, file, fileError: undefined } : item))
    );
    if (!file) return;

    // Track this scan so the UI can disable actions until all PDF scanning is done.
    setScanningCount((c) => c + 1);
    try {
      // Validate the PDF up front. If it is invalid, flag the error and stop —
      // do not attempt any autofill/extraction on a corrupt or non-PDF file.
      const validation = await validatePDFFile(file, { deep: false });
      if (!validation.isValid) {
        setUploadItems((prev) =>
          prev.map((item) => (item.id === id ? { ...item, fileError: validation.error } : item))
        );
        return;
      }

      const isFirstUpload = id === 1;
      const shouldExtractMissingFields = isFirstUpload || needsAdditionalExtractionRef.current;
      let localClassification: Awaited<ReturnType<typeof classifyPDFDocumentType>> | null = null;
      let localDocumentType = '';
      // Only the first upload also needs field extraction (see below). When that's
      // the case, analyze the file once and reuse the result for both, instead of
      // parsing/OCR-ing the same PDF twice.
      let sharedExtraction: ExtractedFieldsWithQuality | null = null;
      try {
        if (shouldExtractMissingFields) {
          const analysis = await analyzePdfDocument(file);
          localClassification = analysis.classification;
          sharedExtraction = analysis.extraction;
          setUploadItems((prev) =>
            prev.map((item) => (item.id === id ? { ...item, pageCount: analysis.pageCount } : item))
          );
        } else {
          localClassification = await classifyPDFDocumentType(file, (pageCount) => {
            setUploadItems((prev) =>
              prev.map((item) => (item.id === id ? { ...item, pageCount } : item))
            );
          });
        }
        localDocumentType = normalizeDocumentTypeForUpload(localClassification.documentType);
        if (localDocumentType) {
          // For the 1st upload we also derive Other Doc Type / Mail Type from the
          // classification. For the 2nd+ uploads we only set the document type —
          // all other fields/info are extracted from the 1st file only.
          setUploadItems((prev) =>
            prev.map((item) =>
              item.id === id
                ? {
                    ...item,
                    selectedDocType: localDocumentType,
                    mailType: getMailTypeForDocument(localDocumentType),
                    ...(isFirstUpload
                      ? {
                          otherDocType: localDocumentType === 'Other' ? item.otherDocType : '',
                        }
                      : {}),
                  }
                : item
            )
          );
          console.log('Local PDF document type classification:', localClassification);
        }
      } catch (classificationErr) {
        console.warn('Local PDF document type classification failed:', classificationErr);
      }

      // Continue filling missing inventory fields from subsequent PDFs until the
      // same validation used by Save reports that all required data is complete.
      if (shouldExtractMissingFields) {
        if (isFirstUpload) {
          const resetData = {
            ...inventoryDataRef.current,
          accountNumber: '',
          mrn: '',
          payerName: '',
          address: '',
          city: '',
          state: '',
          zip: '',
          firstName: '',
          lastName: '',
          clientName: defaultUserClientName,
          facilityAddressId: 0,
          facilityAddress: '',
          facilityCity: '',
          facilityState: '',
          facilityZip: '',
          facilityCode: '',
          facilityName: '',
          facilityPhoneNo: '',
          sendFrom: '',
          region: '',
          };
          inventoryDataRef.current = resetData;
          setdata(resetData);
          setAutofillMissed(new Set());
        }
        setExtracting(true);
        setScanStatusMessage('Extracting PDF...');
        try {
          let extractionResult: ExtractedFieldsWithQuality = sharedExtraction ?? EMPTY_EXTRACTED_RESULT;
          let aiResult: DocumentClassificationResult | null = null;
          const isLocallyClassifiedPatientLetter = normalizeDocumentTypeForUpload(
            localClassification?.documentType,
          ) === 'Patient Letter';

          if (!sharedExtraction) {
            try {
              extractionResult = await extractFieldsFromPDFPageWithQuality(file, {
                excludedFields: isLocallyClassifiedPatientLetter
                  ? ['address', 'city', 'state', 'zip']
                  : [],
              });
            } catch (extractErr) {
              console.warn('PDF field extraction failed; falling back to Azure OpenAI:', extractErr);
            }
          }
          const extracted: ExtractedFields = extractionResult.fields;
          const fieldScores = extractionResult.fieldScores;
          const hasAcceptedLocalField = (field: keyof ExtractedFields) =>
            !!extracted[field] && (fieldScores[field] ?? 0) >= AUTOFILL_QUALITY_THRESHOLD;
          const hasAcceptedLocalPayerAddress = ['address', 'city', 'state', 'zip'].every(
            (field) => hasAcceptedLocalField(field as keyof ExtractedFields)
          );
          const acceptedLocalValues: ExtractedFields = {
            accountNumber: hasAcceptedLocalField('accountNumber') ? extracted.accountNumber : undefined,
            mrn: hasAcceptedLocalField('mrn') ? extracted.mrn : undefined,
            payerName: hasAcceptedLocalField('payerName') ? extracted.payerName : undefined,
            address: hasAcceptedLocalPayerAddress ? extracted.address : undefined,
            city: hasAcceptedLocalPayerAddress ? extracted.city : undefined,
            state: hasAcceptedLocalPayerAddress ? extracted.state : undefined,
            zip: hasAcceptedLocalPayerAddress ? extracted.zip : undefined,
            facilityName: extracted.facilityName,
            firstName: extracted.firstName,
            lastName: extracted.lastName,
          };
          console.log('extracted data from PDF:', {
            fileName: file.name,
            documentTypeClassification: localClassification,
            extractedFields: extracted,
            extractionQuality: extractionResult,
          });
          if (AZURE_AI_CLASSIFICATION_ENABLED && (
            !acceptedLocalValues.accountNumber ||
            !acceptedLocalValues.mrn ||
            (!isLocallyClassifiedPatientLetter && !hasAcceptedLocalPayerAddress) ||
            !extracted.facilityName
          )) {
            setScanStatusMessage('Scanning PDF...');
            try {
              const pageImages = await convertPDFPagesToBase64Json(file);
              aiResult = await classifyDocument(pageImages);
              console.log('Azure OpenAI classification result:', aiResult);
              const aiDocumentType = normalizeDocumentTypeForUpload(aiResult?.classification_category);
              if ((!localDocumentType || localDocumentType === 'Other') && aiDocumentType) {
                setUploadItems((prevItems) =>
                  prevItems.map((item) => item.id === id
                    ? {
                        ...item,
                        selectedDocType: aiDocumentType,
                        mailType: getMailTypeForDocument(aiDocumentType),
                      }
                    : item)
                );
              }
            } catch (aiErr) {
              console.warn('Azure OpenAI classification skipped (token unavailable or call failed):', aiErr);
            }
          }

          const aiPatientNameParts = getPatientNameParts(aiResult?.patient_name);
          const isPatientLetterResult = isLocallyClassifiedPatientLetter ||
            aiResult?.classification_category === 'Patient Letter';
          const finalPayerName = aiResult?.payer_name || acceptedLocalValues.payerName;
          const finalPayerAddress = aiResult?.payer_address || acceptedLocalValues.address;
          const finalPayerCity = aiResult?.payer_city || acceptedLocalValues.city;
          const finalPayerState = aiResult?.payer_state || acceptedLocalValues.state;
          const finalPayerZip = aiResult?.payer_zip || acceptedLocalValues.zip;
          const isPayerRequestType = !isPatientLetterResult && isPayerRequestDocument(
            aiResult?.classification_category || localClassification?.documentType,
            finalPayerName,
            finalPayerAddress,
            finalPayerCity,
            finalPayerState,
            finalPayerZip,
          );
          const extractedFacility: ExtractedFacilityData = {
            name: aiResult?.facility_name || extracted.facilityName,
            address: aiResult?.facility_address,
            city: aiResult?.facility_city,
            state: aiResult?.facility_state,
            zip: aiResult?.facility_zip,
          };
          const matchedFacility = findMatchedFacility(extractedFacility);
          const previousData = inventoryDataRef.current;
          const nextData = {
            ...previousData,
            accountNumber: previousData.accountNumber || aiResult?.patient_account_number || acceptedLocalValues.accountNumber || '',
            mrn: previousData.mrn || aiResult?.mrn || acceptedLocalValues.mrn || '',
            payerName: previousData.payerName || finalPayerName || '',
            displayName: previousData.displayName || aiResult?.display_name || '',
            address: previousData.address || (isPatientLetterResult ? '' : finalPayerAddress) || '',
            city: previousData.city || (isPatientLetterResult ? '' : finalPayerCity) || '',
            state: previousData.state || (isPatientLetterResult ? '' : finalPayerState) || '',
            zip: previousData.zip || (isPatientLetterResult ? '' : finalPayerZip) || '',
            firstName: previousData.firstName || (isPatientLetterResult && aiResult?.patient_name
              ? aiPatientNameParts.firstName || extracted.firstName || ''
              : extracted.firstName || ''),
            lastName: previousData.lastName || (isPatientLetterResult && aiResult?.patient_name
              ? aiPatientNameParts.lastName || extracted.lastName || ''
              : extracted.lastName || ''),
            requestType: previousData.requestType || (isPatientLetterResult ? 'Patient' : isPayerRequestType ? 'Payer' : ''),
            sendTo: previousData.sendTo || (isPatientLetterResult ? 'Patient' : isPayerRequestType ? 'Payer' : ''),
            clientName: previousData.clientName || defaultUserClientName,
            facilityAddressId: previousData.facilityAddressId || matchedFacility?.facilityAddressId || 0,
            facilityName: previousData.facilityName || matchedFacility?.facilityName || '',
            facilityAddress: previousData.facilityAddress || matchedFacility?.lockboxAddress || '',
            facilityCity: previousData.facilityCity || matchedFacility?.lockboxCity || '',
            facilityState: previousData.facilityState || matchedFacility?.lockboxState || '',
            facilityZip: previousData.facilityZip || matchedFacility?.lockboxZip || '',
            facilityCode: previousData.facilityCode || matchedFacility?.facilityCode.toString() || '',
            facilityPhoneNo: previousData.facilityPhoneNo || matchedFacility?.lockboxPhone || '',
            sendFrom: previousData.sendFrom || matchedFacility?.efrFacilityName || '',
            region: previousData.region || matchedFacility?.region.toUpperCase() || '',
          };
          inventoryDataRef.current = nextData;
          setdata(nextData);
          setFacilityCode(nextData.facilityCode);
          if (nextData.sendTo) {
            setAddressFields(nextData.sendTo, nextData.requestType);
          }
          const remainingErrors = getValidationErrors(
            nextData,
            getClientValidationConfig(nextData.clientName.trim()),
          );
          needsAdditionalExtractionRef.current = Object.values(remainingErrors).some(Boolean);
          // Track which fields the PDF could not supply so we can prompt the user.
          const missed = new Set<string>(
            Object.entries(remainingErrors)
              .filter(([, error]) => Boolean(error))
              .map(([field]) => field === 'facilityAddressId' ? 'facilityName' : field)
          );
          setAutofillMissed(missed);
        } catch {
          // Extraction failure is non-fatal — user fills in manually
        } finally {
          setScanStatusMessage('');
          setExtracting(false);
        }
      }
    } finally {
      setScanningCount((c) => c - 1);
    }
  }, [defaultUserClientName, findMatchedFacility, getPatientNameParts]);

  const processIncomingFiles = useCallback((incomingFiles: File[]) => {
    const pdfFiles: File[] = [];
    incomingFiles.forEach((file) => {
      if (file.type !== 'application/pdf') {
        message.error(`File "${file.name}" is not a PDF. Only PDF files are allowed.`);
        return;
      }
      pdfFiles.push(file);
    });

    if (pdfFiles.length === 0) return;

    const currentItems = uploadItemsRef.current;
    const assignments: { id: number; file: File }[] = [];
    let nextItems = [...currentItems];

    pdfFiles.forEach((file) => {
      const lastItem = nextItems[nextItems.length - 1];
      if (!lastItem.file) {
        nextItems[nextItems.length - 1] = { ...lastItem, file, fileError: undefined };
        assignments.push({ id: lastItem.id, file });
      } else {
        const id = nextItems.length + 1;
        nextItems = [
          ...nextItems,
          { id, selectedDocType: '', mailType: '', file, otherDocType: '', fileError: undefined },
        ];
        assignments.push({ id, file });
      }
    });

    setUploadItems(nextItems);

    void (async () => {
      for (const { id, file } of assignments) {
        await handleFileChange(id, file);
      }
    })();
  }, [handleFileChange]);

  // Drag and drop handlers
  const handleDrag = (e: React.DragEvent) => {
      // Highlight drop zone when files dragged over
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleZoneFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    processIncomingFiles(Array.from(files));
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
      // Handle dropped files - validate they're PDFs and add to upload list
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processIncomingFiles(Array.from(files));
    }
  };

  const handleUpload = useCallback(async (inventoryMasterIdOverride?: number, transactionIdOverride?: string) => {
    const effectiveMasterId = inventoryMasterIdOverride ?? data.inventoryMasterId;
    const effectiveTransactionId = transactionIdOverride ?? data.transactionId;
    if (!effectiveMasterId || !effectiveTransactionId) {
      console.error('Skipping file upload because inventory identifiers are incomplete.', {
        effectiveMasterId,
        effectiveTransactionId,
        inventoryMasterId: data.inventoryMasterId,
        transactionId: data.transactionId,
      });
      message.error('The inventory record was saved, but the upload identifiers were not returned. Please reopen the record and try the file upload again.');
      return false;
    }

    for (const item of uploadItems) {
      const documentType = item.selectedDocType === 'Other' ? item.otherDocType : item.selectedDocType;
      if (!documentType) {
        alert(`Please select or enter a document type for item ${item.id}!`);
        return;
      }
      if (!item.mailType) {
        alert(`Please select a mail type for item ${item.id}!`);
        return;
      }
      if (!item.file) {
        alert(`Please select a file for item ${item.id}!`);
        return;
      }
    }

    if (uploadItems.some(item => item.selectedDocType === 'Package')) {
      const userConfirmed = window.confirm(
        'Warning: The "Package" document type should only be selected for request type "Payer" and must include a cover page. Do you want to continue?'
      );
      if (!userConfirmed) return;
    }

    const selectedHierarchies = uploadItems.map(item => {
      const docType = item.selectedDocType === 'Other' ? item.otherDocType : item.selectedDocType;
      return docType && typeof docType === 'string' ? docTypeHierarchy[docType] || 1 : 1;
    });
    const maxHierarchy = Math.max(...selectedHierarchies);

    if (maxHierarchy === 3 && uploadItems.length < maxHierarchy) {
      message.error(`At least ${maxHierarchy} document(s) are required based on the selected document types.`);
      return;
    }

    const hasHierarchy2 = uploadItems.some(item =>
      typeof item.selectedDocType === 'string' && docTypeHierarchy[item.selectedDocType] === 2
    );
    const hasHierarchy1 = uploadItems.some(item =>
      typeof item.selectedDocType === 'string' && docTypeHierarchy[item.selectedDocType] === 1
    );

    if (hasHierarchy2 && !hasHierarchy1) {
      const errorMessage = data.requestType === 'Patient'
        ? 'For Patient Request Type: This document requires an additional document type of Patient Letter.'
        : 'For Payer Request Type: This document requires an additional document type of Payer Letter or Cover Letter.';
      message.error(errorMessage);
      return;
    }

    const invalidFiles = uploadItems.filter((item) => {
      if (!item.file) return true;
      const fileExtension = item.file?.name.split('.').pop()?.toLowerCase();
      return fileExtension !== 'pdf';
    });
    if (invalidFiles.length > 0) {
      message.error('All uploaded files must be in .pdf format. Please remove invalid files and try again.');
      return;
    }

    setValidating(true);
    const fileValidationErrors: string[] = [];
    for (const item of uploadItems) {
      if (item.file) {
        const validationResult = await validatePDFFile(item.file, { deep: true });
        if (!validationResult.isValid) {
          const docLabel = item.selectedDocType === 'Other' ? item.otherDocType : item.selectedDocType;
          fileValidationErrors.push(`"${item.file.name}" (${docLabel || 'unspecified'}): ${validationResult.error}`);
        }
      }
    }
    setValidating(false);

    if (fileValidationErrors.length > 0) {
      fileValidationErrors.forEach((err) => message.error(err));
      return;
    }

    let uploadSucceeded = false;

    setRefreshList(false);
    setUploading(true);

    try {
      const baseurl = process.env.REACT_APP_RRD_OPTIMIZATION_BASE_URL as string;
      const payload = {
        inventoryMasterId: effectiveMasterId,
        transactionStatus: 2,
        componentName: "Nova UI",
        componentStatus: "CS",
        remarks: "File uploading complete.",
        modifiedBy: userid,
        modifiedOn: "9999-12-12 00:00:00.000"
      };

      for (const item of uploadItems) {
        const doctypeabbreviation = getPackageAbbreviation(item.selectedDocType || '', data.requestType);
        const mtype = item.mailType;
        const fileNamePrefix = `${doctypeabbreviation}_${mtype}_${facilityCode}_${data.accountNumber}`;
        let doctypeseqnumber = 0;
        const documentType = item.selectedDocType === 'Other' ? item.otherDocType : item.selectedDocType;

        const seqresponse = await axios.get(`${baseurl}/Inventory/SequenceNumber/${fileNamePrefix}`, {
          headers: { 'Content-Type': 'application/json' },
        });
        if (seqresponse.data.success) {
          doctypeseqnumber = parseInt(seqresponse.data.result ? seqresponse.data.result : '1', 10);
        }
        const formattedSequenceNumber = doctypeseqnumber < 10 ? `0${doctypeseqnumber}` : doctypeseqnumber;
        const fname = `${fileNamePrefix}_${formattedSequenceNumber}`;

        const formData = new FormData();
        formData.append('inventoryFileId', '0');
        formData.append('transactionId', effectiveTransactionId?.toString() || '');
        formData.append('documentType', documentType || '');
        formData.append('actualFileName', item.file?.name! || '');
        formData.append('mailType', mtype);
        formData.append('packageName', '');
        formData.append('fileName', fname);
        formData.append('filePath', '');
        formData.append('region', data.region);
        formData.append('createdBy', userid);
        formData.append('file', item.file ? item.file : new Blob());

        console.log('Uploading file with details:', Array.from(formData.entries()).map(([key, value]) => {
          if (value instanceof File) {
            return [key, { name: value.name, size: value.size, type: value.type }];
          }
          return [key, value];
        }));
        const url = `${baseurl}/InventoryFiles/Add`;
        const response = await axios.post(url, formData, {
        });

        if (response.data.success) {
          message.success(`File uploaded successfully for ${item.selectedDocType === 'Other' ? item.otherDocType : item.selectedDocType}!`);
        }
      }
      try {
        const response = await axios.put(`${baseurl}/Inventory/UpdateStatus`, JSON.stringify(payload), {
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.data.success) {
          refreshData();
          uploadSucceeded = true;
        }
      } catch (error: any) {
        message.error('Error updating inventory status:- ' + error.response.data.message);
      }
    } catch (error: any) {
      console.error('Error uploading files:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      message.error('Failed to upload files: ' + (error?.response?.data?.message || error?.message || 'Unknown error'));
    } finally {
      setRefreshList(true);
      setUploading(false);
      setUploadItems([{ id: 1, selectedDocType: '', mailType: '', file: null, otherDocType: '' }]);
    }

    return uploadSucceeded;
  }, [uploadItems, facilityCode, data, userid, refreshData]);

  const getUploadValidationErrors = useCallback((): string[] => {
    return uploadItems.flatMap((item) => {
      const errors: string[] = [];
      const mailTypeEnabled = !(
        data.requestType === 'Patient' && item.selectedDocType === 'Patient Letter'
      );

      if (!item.file) {
        errors.push(`Document ${item.id}: PDF file is required`);
      } else if (item.fileError) {
        errors.push(`Document ${item.id}: ${item.fileError}`);
      }
      if (!item.selectedDocType.trim()) {
        errors.push(`Document ${item.id}: Document Type is required`);
      }
      if (item.selectedDocType === 'Other' && !item.otherDocType.trim()) {
        errors.push(`Document ${item.id}: Document Name is required`);
      }
      if (mailTypeEnabled && !item.mailType.trim()) {
        errors.push(`Document ${item.id}: Mail Type is required`);
      }

      return errors;
    });
  }, [data.requestType, uploadItems]);

  const handleSaveAndUpload = async () => {
    const currentErrors = validateFields();
    const inventoryErrors = currentErrors ? Object.values(currentErrors).filter(Boolean) : [];
    const uploadErrors = getUploadValidationErrors();
    const allErrors = [...inventoryErrors, ...uploadErrors];
    if (allErrors.length > 0) {
      message.error('Validation Errors: ' + allErrors.join(', '));
      return;
    }

    if (inProgressRecords.length > 0) {
      const ok = window.confirm(
        'A record with this account number already exists and is currently In\u2011Progress. Would you like to continue and create another inventory entry?'
      );
      if (!ok) return;
    }

    const hasFiles = uploadItems.some((i) => i.file);
    const savedIds = await saveDetails();
    if (!savedIds) {
      return;
    }

    const uploadSucceeded = hasFiles
      ? await handleUpload(savedIds.inventoryMasterId, savedIds.transactionId)
      : true;

    if (uploadSucceeded) {
      setIsOpen(false);
    }
  };

  const handleRemoveFile = useCallback(async (id: number) => {
    const userConfirmed = window.confirm('Are you sure you want to delete this file?');
    if (userConfirmed) {
      setRefreshList(false);
      try {
        const baseurl = process.env.REACT_APP_RRD_OPTIMIZATION_BASE_URL as string;
        const url = `${baseurl}/InventoryFiles/Delete/${id}/${userid}`;
        const response = await axios.delete(url, {
          headers: { 'accept': 'text/plain' },
        });
        if (response.data.success) {
          setRefreshList(true);
          message.success('File deleted successfully!');
        }
      } catch (error: any) {
        message.error('error deleting file.');
        console.log('error deleting file:- ', error);
      }
    }
  }, [userid]);

  const validateFields = (): ValidationErrors | null => {
    const newErrors = getValidationErrors(data, clientValidationConfig);
    setValidationErrors(newErrors);

    return Object.values(newErrors).every((error) => error === '') ? null : newErrors;
  };

  const saveDetails = async (): Promise<{ inventoryMasterId: number; transactionId: string } | null> => {
    const baseurl = process.env.REACT_APP_RRD_OPTIMIZATION_BASE_URL as string;
    const axiosMethod = data.inventoryMasterId > 0 ? axios.put : axios.post;
    const endpoints = data.inventoryMasterId > 0 ? '/Inventory/UpdateInventoryDetails' : '/Inventory/Add';

    const payload = {
      inventoryMasterId: data.inventoryMasterId,
      clientName: data.clientName,
      region: data.region,
      sourceSystem: "RAV",
      sourceInventoryId: "NA",
      transactionStatus: data.inventoryMasterId > 0 ? 2 : 1,
      componentName: "RAV",
      remarks: data.inventoryMasterId > 0 ? "Inventory edited and renewed" : "Inventory created",
      createdBy: userid,
      modifiedBy: userid,
      isActive: true,
      requestType: data.requestType,
      claimInformation: {
        accountNumber: data.accountNumber.trim(),
        mrn: data.mrn,
        claimNo: data.claimNumber.trim(),
        patientName: "",
        patientDOB: ""
      },
      senderInformation: {
        sendFrom: data.sendFrom,
        name: data.facilityName,
        address: data.facilityAddress,
        city: data.facilityCity,
        state: data.facilityState,
        zip: data.facilityZip,
        code: data.facilityCode,
        phoneNo: ''
      },
      receiverInformation: {
        displayName: data.displayName.trim(),
        sendTo: data.sendTo.trim(),
        name: data.sendTo === 'Override Address' ? data.lastName.trim() + ', ' + data.firstName.trim() : data.payerName.trim(),
        address: data.address.trim(),
        city: data.city.trim(),
        state: data.state.trim(),
        zip: data.zip.trim(),
        code: "",
        phoneNo: ""
      }
    };

    setUploading(true);
    try {
      const response = await axiosMethod(`${baseurl}${endpoints}`, JSON.stringify(payload), {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data.success) {
        let resolvedMasterId = data.inventoryMasterId;
        let resolvedTransactionId = data.transactionId;

        if (data.inventoryMasterId < 1) {
          const inventoryResponse = await axios.get(`${baseurl}/Inventory/${response.data.result}`, {
            headers: { 'Content-Type': 'application/json' }
          });
          if (inventoryResponse.data.success) {
            resolvedMasterId = inventoryResponse.data.result?.inventoryMasterId || 0;
            resolvedTransactionId = inventoryResponse.data.result?.transactionId || '';
            setdata((prevdata) => ({
              ...prevdata,
              inventoryMasterId: resolvedMasterId,
              transactionId: resolvedTransactionId,
            }));
          }
        }

        message.success('Data saved successfully!');
        if (refreshData) refreshData();
        return { inventoryMasterId: resolvedMasterId, transactionId: resolvedTransactionId };
      }
      return null;
    } catch (error: any) {
      message.error('Error saving data:- ' + error.response.data.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const clientNameOptions = userClientNames.map((clientName) => ({
    label: clientName,
    value: clientName,
  }));

  const clientValidationConfig = useMemo(
    () => getClientValidationConfig(data.clientName.trim()),
    [data.clientName],
  );

  const liveValidationErrors = useMemo(
    () => getValidationErrors(data, clientValidationConfig),
    [data, clientValidationConfig],
  );

  const isFieldRequired = useCallback((field: ValidationField) => {
    if (clientValidationConfig.requiredFields.includes(field)) {
      return true;
    }
    if (field === 'payerName') {
      return data.sendTo.trim() === 'Payer';
    }
    if (['address', 'city', 'state', 'zip'].includes(field)) {
      return ADDRESS_REQUIRED_SEND_TO.includes(data.sendTo.trim());
    }
    if (OVERRIDE_ADDRESS_REQUIRED_FIELDS.includes(field)) {
      return data.sendTo.trim() === 'Override Address';
    }
    return false;
  }, [clientValidationConfig.requiredFields, data.sendTo]);

  useEffect(() => {
    if (Object.values(validationErrors).some(Boolean)) {
      setValidationErrors(liveValidationErrors);
    }
  }, [liveValidationErrors, validationErrors]);

  // True while any PDF scan/validation/extraction is in progress. Used to
  // disable all actions and show a scanning message until scanning completes.
  const isScanning = scanningCount > 0 || extracting;

  // True when the form has any blocking issue and must not be submitted:
  //  - a required field is empty,
  //  - a field has an invalid format (state / zip),
  //  - an upload row is missing its PDF or required metadata, or
  //  - there is any active validation error message.
  // Keeps the Save / Save & Upload button disabled until everything is valid.
  const isFormInvalid = useMemo(() => {
    if (getUploadValidationErrors().length > 0) return true;

    return Object.values(liveValidationErrors).some(Boolean);
  }, [getUploadValidationErrors, liveValidationErrors]);
//console.log('Autofill fields', autofillMissed)
  // Render
  return (
    <div>
      <Modal
        dataTestId="RRDCombinedModal-modalHeader"
        isOpen={isOpen}
        onClickOut={() => setIsOpen(false)}
        size="fullScreen"
        domID="RRDCombined-modal"
        closeModalOnClickOut={true}
      >
        <ModalHeader
          title="Add/Edit Inventory"
          dataTestId="test-header"
          description="Upload PDF documents and fill in inventory details below. The first PDF's front page will auto-fill matching fields."
          onClose={() => setIsOpen(false)}
        />
        <ModalBody>
          <Container>
            {/* ── PDF Upload Section ── */}
            <div className="rrd-upload-section">
              <p className="rrd-section-title">
                Upload Documents{isScanning && <span className="rrd-scanning-inline">{scanStatusMessage || 'Scanning PDF...'}</span>}
              </p>

              {/* Scanning banner — clearly tells the user actions are locked until scan completes */}
              {isScanning && (
                <div
                  role="status"
                  aria-live="polite"
                  className="rrd-scan-banner"
                >
                  <span className="rrd-scan-spinner" />
                  {scanStatusMessage || 'Scanning PDF...'} Please wait — Save, Upload, and Add More are
                  disabled until all documents finish scanning.
                </div>
              )}

              {/* Drag and Drop Zone — click anywhere or use Browse button to pick files */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                multiple
                disabled={isScanning}
                className="rrd-hidden"
                onChange={handleZoneFileSelect}
                aria-label="Upload PDF documents"
                title="Upload PDF documents"
              />
              <div
                ref={dragRef}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => { if (!isScanning) fileInputRef.current?.click(); }}
                className={`rrd-dropzone${dragActive ? ' rrd-dropzone--active' : ''}${isScanning ? ' rrd-dropzone--scanning' : ''}`}
              >
                <p className="rrd-dropzone-text">Drag and drop PDF files here, or</p>
                <span className="rrd-browse-btn">
                  Browse files
                </span>
                <p className="rrd-dropzone-hint">Supported format: PDF only · First file auto-fills form fields</p>
              </div>

              {/* Upload Items */}
              {uploadItems.map((item) => (
                <div
                  key={item.id}
                  className="rrd-upload-item"
                >
                  <Row className="rrd-row-center">
                    <Col sm={4}>
                      <label htmlFor={`doctype-${item.id}`} className="rrd-label-bold uicl-label">
                        Document Type <span className="rrd-required">*</span>
                      </label>
                      <SelectDropdown
                        ariaLabel="ARIA label"
                        dataTestId={`doctype-selectDropdown-${item.id}`}
                        domID={`doctype-${item.id}`}
                        placeholderText="Select..."
                        isClearable={false}
                        zIndex={99}
                        options={activeDocTypeOptions}
                        initialValue={{ label: item.selectedDocType, value: item.selectedDocType }}
                        onChange={(e) => handleItemChange(item.id, 'selectedDocType', e?.value ?? '')}
                      />
                      {item.selectedDocType === 'Other' && (
                        <div className="rrd-mt-8">
                          <label htmlFor={`otherDocType-${item.id}`} className="rrd-label-bold uicl-label">
                            Document Name <span className="rrd-required">*</span>
                          </label>
                          <Input
                            domID={`otherDocType-${item.id}`}
                            aria-label="Enter Document Name"
                            placeholder="Enter document name..."
                            value={item.otherDocType || ''}
                            onChange={(e) => handleItemChange(item.id, 'otherDocType', e.target.value)}
                            className="rrd-input-full"
                          />
                        </div>
                      )}
                    </Col>
                    <Col sm={3}>
                      <label htmlFor={`mailtype-${item.id}`} className="rrd-label-bold uicl-label">
                        Mail Type <span className="rrd-required">*</span>
                      </label>
                      <SelectDropdown
                        ariaLabel="ARIA label"
                        dataTestId={`mailtype-selectDropdown-${item.id}`}
                        domID={`mailtype-${item.id}`}
                        placeholderText="Select..."
                        isClearable={false}
                        zIndex={99}
                        disabled={data.requestType === 'Patient' && item.selectedDocType === 'Patient Letter'}
                        options={mailTypeOptions}
                        initialValue={{ label: item.mailType, value: item.mailType }}
                        onChange={(e) => handleItemChange(item.id, 'mailType', e?.value ?? '')}
                      />
                    </Col>
                    <Col sm={4} className="rrd-file-status">
                      {item.fileError && (
                        <div className="rrd-file-error">⚠ {item.fileError}</div>
                      )}
                      {item.file && !item.fileError && (
                        <div className="rrd-file-success">
                          ✓ {item.file.name}
                          {item.pageCount !== undefined && (
                            <span className="rrd-page-count"> &nbsp;|&nbsp; Total Pages:- {item.pageCount}</span>
                          )}
                        </div>
                      )}
                      {!item.file && !item.fileError && (
                        <div className="rrd-file-none">No file selected</div>
                      )}
                      {item.file && item.id === firstUploadedItemId && (
                        <Button
                          buttonType="diminished"
                          disabled={isScanning}
                          onClick={() => handleClearFile(item.id)}
                          className="rrd-mt-6"
                        >
                          Remove File
                        </Button>
                      )}
                    </Col>
                    <Col sm={1} className="rrd-remove-col">
                      {item.id !== 1 && (
                        <Button buttonType="diminished" disabled={isScanning} onClick={() => handleRemoveItem(item.id)}>Remove</Button>
                      )}
                    </Col>
                  </Row>
                </div>
              ))}

              {/* <Button buttonType="emphasized" disabled={isScanning} onClick={handleAddMore}>+ Add More</Button> */}
            </div>

            <hr className="rrd-divider" />

            {/* ── Inventory Details Form ── */}
            <div>
                {/* Client Name */}
                <div className="rrd-field">
                  <label htmlFor="clientname-id" className="rrd-label uicl-label">
                    Client Name {isFieldRequired('clientName') && <span className="rrd-required">*</span>}
                  </label>
                  <SelectDropdown
                    key={'clientname-' + data.clientName}
                    ariaLabel="ARIA label"
                    dataTestId="clientname-selectDropdownBasic"
                    domID="clientname-id"
                    placeholderText="Select..."
                    isClearable={false}
                    zIndex={99}
                    initialValue={{ label: data.clientName, value: data.clientName }}
                    onChange={(e) => handleChange(e, 'clientName')}
                    options={clientNameOptions}
                    disabled={data.inventoryMasterId > 0 ? true : false}
                  />
                  {validationErrors.clientName && (
                    <span className="rrd-error-text">{validationErrors.clientName}</span>
                  )}
                  {autofillMissed.has('clientName') && (
                    <span className="rrd-warning-text">Could not detect from PDF — please select manually</span>
                  )}
                </div>

                {/* Facility Address */}
                <div className="rrd-field">
                  <label htmlFor="Facility Address" className="rrd-label uicl-label">
                    Facility Address {isFieldRequired('facilityAddressId') && <span className="rrd-required">*</span>}
                  </label>
                  <SelectDropdown
                    key={'facility-' + data.facilityAddressId}
                    menuWidth={800}
                    domID="Facility Address"
                    aria-label="Facility Address"
                    dataTestId="facilityaddress-selectDropdownBasic"
                    size="medium"
                    placeholderText="Billing Lockbox Address"
                    isClearable={false}
                    initialValue={{ label: (data.facilityName + ' - ' + data.facilityAddress + ' - ' + data.facilityCity + ' - ' + data.facilityState + ' - ' + data.facilityZip), value: data.facilityAddressId.toString() }}
                    onChange={(e) => handleChange(e, 'lockboxAddress')}
                    disabled={data.inventoryMasterId > 0 ? true : false}
                    customFilter={(option, searchText) => {
                      const lowerCaseSearchText = searchText.toLowerCase();
                      return (
                        option?.label?.toLowerCase().includes(lowerCaseSearchText) ||
                        option?.value?.toLowerCase().includes(lowerCaseSearchText)
                      ) || false;
                    }}
                    options={availableFacilityOptions}
                  />
                  {validationErrors.facilityAddressId && (
                    <span className="rrd-error-text">{validationErrors.facilityAddressId}</span>
                  )}
                  {autofillMissed.has('facilityName') && (
                    <span className="rrd-warning-text">Could not detect from PDF — please select manually</span>
                  )}
                </div>

                {/* Region */}
                <div className="rrd-field">
                  <label htmlFor="region-id" className="rrd-label uicl-label">
                    Region {isFieldRequired('region') && <span className="rrd-required">*</span>}
                  </label>
                  <SelectDropdown
                    key={'region-' + data.region}
                    ariaLabel="ARIA label"
                    dataTestId="region-selectDropdownBasic"
                    domID="region-id"
                    placeholderText="Select..."
                    onChange={(e) => handleChange(e, 'region')}
                    isClearable={false}
                    zIndex={99}
                    initialValue={{ label: data.region, value: data.region }}
                    disabled={data.inventoryMasterId > 0 ? true : false}
                    options={arrregioncodes.map(code => ({ label: code, value: code }))}
                  />
                  {validationErrors.region && (
                    <span className="rrd-error-text">{validationErrors.region}</span>
                  )}
                  {autofillMissed.has('region') && (
                    <span className="rrd-warning-text">Could not detect from PDF — please select manually</span>
                  )}
                </div>

                {/* Account Number */}
                <div className="rrd-field">
                  <label htmlFor="pan-number" className="rrd-label uicl-label">
                    Patient Account Number {isFieldRequired('accountNumber') && <span className="rrd-required">*</span>}
                  </label>
                  <Input
                    domID="pan-number"
                    aria-label="Patient Account Number"
                    size="medium"
                    placeholder="Patient Account Number"
                    initialValue={data.accountNumber}
                    onChange={(e) => handleChange(e.target, 'accountNumber')}
                    disabled={data.inventoryMasterId > 0 ? true : false}
                  />
                  {validationErrors.accountNumber && (
                    <span className="rrd-error-text">{validationErrors.accountNumber}</span>
                  )}
                  {autofillMissed.has('accountNumber') && (
                    <span className="rrd-error-text">Unable to fill, please fill out manually</span>
                  )}
                </div>

                {/* Claim Number */}
                <div className="rrd-field">
                  <label htmlFor="claim-number" className="rrd-label uicl-label">
                    Claim Number
                  </label>
                  <Input
                    domID="claim-number"
                    aria-label="Claim Number"
                    size="medium"
                    placeholder="Claim Number"
                    initialValue={data.claimNumber}
                    onChange={(e) => handleChange(e.target, 'claimNumber')}
                  />
                </div>

                {/* MRN */}
                <div className="rrd-field">
                  <label htmlFor="MRN" className="rrd-label uicl-label">
                    MRN {isFieldRequired('mrn') && <span className="rrd-required">*</span>}
                  </label>
                  <Input
                    domID="MRN"
                    aria-label="MRN"
                    size="medium"
                    placeholder="MRN"
                    initialValue={data.mrn}
                    onChange={(e) => handleChange(e.target, 'mrn')}
                  />
                  {validationErrors.mrn && (
                    <span className="rrd-error-text">{validationErrors.mrn}</span>
                  )}
                  {autofillMissed.has('mrn') && (
                    <span className="rrd-error-text">Unable to fill, please fill out manually</span>
                  )}
                </div>

                {/* Request Type */}
                <div className="rrd-field">
                  <label htmlFor="requesttype-id" className="rrd-label uicl-label">
                    Request Type {isFieldRequired('requestType') && <span className="rrd-required">*</span>}
                  </label>
                  <SelectDropdown
                    ariaLabel="ARIA label"
                    dataTestId="requesttype-selectDropdownBasic"
                    domID="requesttype-id"
                    placeholderText="Select..."
                    isClearable={false}
                    zIndex={99}
                    initialValue={{ label: data.requestType, value: data.requestType }}
                    onChange={(e) => handleRequestTypeChange(e)}
                    options={[
                      { label: 'Insurance/Payer', value: 'Payer' },
                      { label: 'Patient', value: 'Patient' }
                    ]}
                  />
                  {validationErrors.requestType && (
                    <span className="rrd-error-text">{validationErrors.requestType}</span>
                  )}
                </div>

                {/* Send To */}
                <div className="rrd-field">
                  <label htmlFor="sendto-id" className="rrd-label uicl-label">
                    Send To {isFieldRequired('sendTo') && <span className="rrd-required">*</span>}
                  </label>
                  <SelectDropdown
                    ariaLabel="ARIA label"
                    dataTestId="sendto-selectDropdownBasic"
                    domID="sendto-id"
                    placeholderText="Select..."
                    isClearable={false}
                    zIndex={99}
                    initialValue={{ label: data.sendTo, value: data.sendTo }}
                    options={
                      data.requestType === 'Patient'
                        ? [{ label: 'Patient', value: 'Patient' },
                          { label: 'Override Address', value: 'Override Address' }
                        ]
                        : [
                          { label: 'Payer', value: 'Payer' },
                          { label: 'Patient', value: 'Patient' },
                          { label: 'Override Address', value: 'Override Address' },
                        ]
                    }
                    onChange={(e) => handleChange(e, 'sendTo')}
                  />
                  {validationErrors.sendTo && (
                    <span className="rrd-error-text">{validationErrors.sendTo}</span>
                  )}
                </div>

                {/* Conditional Address Fields */}
                {(showPayerAddressFields || showBREFields || showPatientFields) && (
                  <div className="rrd-address-section">
                    {/* Payer Name — shown when sendTo=Payer */}
                    {showPayerAddressFields && (
                      <div>
                        <div ref={payerNameDivRef} className="rrd-field-relative">
                          <label htmlFor="payer-name" className="rrd-label uicl-label">
                            Search or Select Payer Name {isFieldRequired('payerName') && <span className="rrd-required">*</span>}
                          </label>
                          <Input
                            domID="payer-name"
                            aria-label="Search Payer Name"
                            size="medium"
                            placeholder={isDignitySelected ? "Search or Select Payer Name" : "Type..."}
                            value={data.payerName}
                            onChange={(e) => {
                              setdata((prevdata) => ({ ...prevdata, payerName: e.target.value }));
                              if (clientFieldConfig.payerNameAutocomplete) setShowDropdown(true);
                              setAutofillMissed((prev) => { const next = new Set(prev); next.delete('payerName'); return next; });
                            }}
                            onClick={() => { if (clientFieldConfig.payerNameAutocomplete) setShowDropdown(true); }}
                            maxLength={50}
                          />
                          {validationErrors.payerName && (
                            <span className="rrd-error-text">{validationErrors.payerName}</span>
                          )}
                          {autofillMissed.has('payerName') && (
                            <span className="rrd-error-text">Unable to fill, please fill out manually</span>
                          )}
                          {/* Autocomplete payer list — only for clients with payerNameAutocomplete enabled */}
                          {clientFieldConfig.payerNameAutocomplete && showDropdown && showPayerAddressFields && (
                            <div
                              ref={payerNameDropdownRef}
                              className="rrd-autocomplete-dropdown"
                            >
                              {filteredOptions.length > 0 ? (
                                filteredOptions.map((option) => (
                                  <div
                                    key={option.value}
                                    className="rrd-autocomplete-option"
                                    onClick={() => handleOptionClick(option)}
                                  >
                                    {option.label}
                                  </div>
                                ))
                              ) : (
                                <div className="rrd-autocomplete-empty">
                                  No options found
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Display Name — only for clients with displayName enabled */}
                        {clientFieldConfig.displayName && (
                          <div className="rrd-field-sm">
                            <label htmlFor="displayName" className="rrd-label uicl-label">
                              Display Name
                            </label>
                            <Input
                              domID="displayName"
                              aria-label="displayName"
                              size="medium"
                              placeholder="Display Name"
                              initialValue={data.displayName}
                              onChange={(e) => handleChange(e.target, 'displayName')}
                              maxLength={50}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* First Name / Last Name — Override Address only, for clients with firstLastName enabled */}
                    {showBREFields && clientFieldConfig.firstLastName && (
                      <Row className="rrd-field-sm">
                        <Col sm={6}>
                          <label htmlFor="firstName" className="rrd-label uicl-label">
                            First Name {isFieldRequired('firstName') && <span className="rrd-required">*</span>}
                          </label>
                          <Input
                            domID="firstName"
                            aria-label="firstName"
                            size="medium"
                            placeholder="First Name"
                            className="rrd-input-full"
                            value={data.firstName}
                            onChange={(e) => handleChange(e.target, 'firstName')}
                          />
                          {validationErrors.firstName && (
                            <span className="rrd-error-text">{validationErrors.firstName}</span>
                          )}
                          {autofillMissed.has('firstName') && (
                            <span className="rrd-error-text">Unable to fill, please fill out manually</span>
                          )}
                        </Col>
                        <Col sm={6}>
                          <label htmlFor="lastName" className="rrd-label uicl-label">
                            Last Name {isFieldRequired('lastName') && <span className="rrd-required">*</span>}
                          </label>
                          <Input
                            domID="lastName"
                            aria-label="lastName"
                            size="medium"
                            placeholder="Last Name"
                            initialValue={data.lastName}
                            onChange={(e) => handleChange(e.target, 'lastName')}
                          />
                          {validationErrors.lastName && (
                            <span className="rrd-error-text">{validationErrors.lastName}</span>
                          )}
                          {autofillMissed.has('lastName') && (
                            <span className="rrd-error-text">Unable to fill, please fill out manually</span>
                          )}
                        </Col>
                      </Row>
                    )}

                    {/* Address / City / State / Zip */}
                    <div className="rrd-field-relative">
                      <Row>
                        <Col sm={6}>
                          <label htmlFor="address" className="rrd-label uicl-label">
                            Address {isFieldRequired('address') && <span className="rrd-required">*</span>}
                          </label>
                          <Input
                            domID="address"
                            aria-label="address"
                            size="medium"
                            placeholder="Address"
                            className="rrd-input-full"
                            value={data.address}
                            maxLength={50}
                            onChange={(e) => {
                              let inputValue = e.target.value;
                              if (inputValue.endsWith(',')) {
                                inputValue = inputValue.slice(0, -1);
                              }
                              setdata({ ...data, address: inputValue });
                              setPayerAddressSearchText(inputValue);
                              setPayerAddressShowDropdown(true);
                              setAutofillMissed((prev) => { const next = new Set(prev); next.delete('address'); return next; });
                            }}
                            onClick={() => data.sendTo === 'Payer' && setPayerAddressShowDropdown(true)}
                          />
                          {autofillMissed.has('address') && (
                            <span className="rrd-error-text">Unable to fill, please fill out manually</span>
                          )}
                          {payerAddressShowDropdown && payerAddressFilteredOptions.length > 0 && clientFieldConfig.payerNameAutocomplete &&(
                            <div
                              ref={payerAddressDropdownRef}
                              className="rrd-autocomplete-dropdown"
                            >
                              {payerAddressFilteredOptions.map((option) => (
                                <div
                                  key={option.value}
                                  className="rrd-autocomplete-option"
                                  onClick={() => handlePayerAddressOptionClick(option)}
                                >
                                  {option.label}
                                </div>
                              ))}
                            </div>
                          )}
                        </Col>
                        <Col sm={6}>
                          <label htmlFor="city" className="rrd-label uicl-label">
                            City {isFieldRequired('city') && <span className="rrd-required">*</span>}
                          </label>
                          <Input
                            domID="city"
                            aria-label="city"
                            size="medium"
                            placeholder="City"
                            className="rrd-input-full"
                            initialValue={data.city}
                            onChange={(e) => handleChange(e.target, 'city')}
                            maxLength={50}
                          />
                          {autofillMissed.has('city') && (
                            <span className="rrd-error-text">Unable to fill, please fill out manually</span>
                          )}
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6}>
                          <label htmlFor="state" className="rrd-label uicl-label">
                            State {isFieldRequired('state') && <span className="rrd-required">*</span>}
                          </label>
                          <Input
                            domID="state"
                            aria-label="state"
                            size="medium"
                            placeholder="State"
                            className="rrd-input-full"
                            initialValue={data.state}
                            onChange={(e) => handleChange(e.target, 'state')}
                            regex='^[A-Z]{2}$'
                            hasError={!/^[A-Z]{2}$/.test(data.state)}
                          />
                          {validationErrors.state && (
                            <span className="rrd-error-text">{validationErrors.state}</span>
                          )}
                          {autofillMissed.has('state') && (
                            <span className="rrd-error-text">Unable to fill, please fill out manually</span>
                          )}
                        </Col>
                        <Col sm={6}>
                          <label htmlFor="zip" className="rrd-label uicl-label">
                            Zip {isFieldRequired('zip') && <span className="rrd-required">*</span>}
                          </label>
                          <Input
                            domID="zip"
                            aria-label="zip"
                            size="medium"
                            placeholder="Zip"
                            className="rrd-input-full"
                            value={data.zip}
                            onChange={(e) => handleChange(e.target, 'zip')}
                            regex="^\d{5}(-\d{4})?$"
                            hasError={!/^\d{5}(-\d{4})?$/.test(data.zip)}
                          />
                          {validationErrors.zip && (
                            <span className="rrd-error-text">{validationErrors.zip}</span>
                          )}
                          {autofillMissed.has('zip') && (
                            <span className="rrd-error-text">Unable to fill, please fill out manually</span>
                          )}
                        </Col>
                      </Row>
                    </div>
                  </div>
                )}
              </div>

            {/* ── Previously Uploaded Files ── */}
            {data.inventoryMasterId > 0 && uploadedFileRecords.length > 0 && (
              <div className="rrd-uploaded-section">
                <p className="rrd-section-title">Previously Uploaded Files</p>
                <Grid
                  columns={columns}
                  dataTestId="test-gridDefault"
                  domID="test-id"
                  records={uploadedFileRecords}
                  supportSelection={false}
                  selectionKey="id"
                />
              </div>
            )}
          </Container>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button buttonType="diminished" name="Cancel" onClick={() => setIsOpen(false)} />
            <Button
              buttonType="emphasized"
              name='Save & Upload'
              disabled={uploading || validating || isScanning || isFormInvalid}
              onClick={handleSaveAndUpload}
            />
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    </div>
  );
});

export default RRDCombinedInventoryModal;
